import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Keeps a scroll container showing its content across a device rotation on iOS Safari.
 *
 * A fast flick leaves the container in momentum (inertial) scrolling, and Safari keeps that scroll
 * session running against the pre-rotation layout. When the rotation then shrinks the content —
 * the settings language grid goes from 3 to 6 columns, ~1075px down to ~578px — the offset ends
 * past the new end of the content and the container shows nothing until the next touch.
 *
 * On every orientation flip: stop the momentum (toggling `overflow` makes Safari drop the running
 * scroll session), then, once the new layout is in, write the offset back clamped to the new
 * content height, which also makes Safari repaint the scrolled layer.
 */
export const useSettleScrollOnRotate = (ref: RefObject<HTMLElement | null>): void => {
  useEffect(() => {
    const mediaQuery = window.matchMedia('(orientation: portrait)');
    let frame = 0;

    const onChange = () => {
      const el = ref.current;
      if (!el) return;

      const scrollTop = el.scrollTop;
      el.style.overflow = 'hidden';
      // Reading a layout property forces `overflow: hidden` to be applied right now. Without a
      // read in between, the two writes are batched into a no-op and the momentum keeps going.
      void el.offsetHeight;
      el.style.overflow = '';
      el.scrollTop = scrollTop;

      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        el.scrollTop = Math.min(el.scrollTop, Math.max(0, el.scrollHeight - el.clientHeight));
      });
    };

    mediaQuery.addEventListener('change', onChange);
    return () => {
      mediaQuery.removeEventListener('change', onChange);
      cancelAnimationFrame(frame);
    };
  }, [ref]);
};
